import React, { createContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
export const BookingContext = createContext({});

const sessionStorageKey = 'bookingContext';

const defaultState = {
  bookingCustomParameters: {},
  fields: {},
  formValues: {},
  ocrValues: {},
  signatures: {},
  holderAddressData: null,
  holderContactData: null,
};

export function BookingContextProvider({ children }) {
  const router = useRouter();
  const [state, setState] = useState(defaultState);
  const actions = {
    clearState: () => {
      updateState(defaultState);
    },
    setBookingSearchForm: bookingSearchForm => {
      updateState({ bookingSearchForm });
    },
    setBookingSearchFormValues: bookingSearchFormValues => {
      if (bookingSearchFormValues.bookingCode) {
        bookingSearchFormValues.bookingCode = bookingSearchFormValues.bookingCode.trim();
      }
      updateState({ bookingSearchFormValues });
    },
    setBookingSearchResults: bookings => {
      updateState({ bookings });
    },
    setBookingToCheckin: bookingToCheckin => {
      updateState({ bookingToCheckin, holderAddressData: null, holderContactData: null });
    },
    setCheckinRequirements: checkinRequirements => {
      // TODO Save on ddbb
      if (state.checkinRequirements && state.checkinRequirements.checkinGuests) {
        checkinRequirements.checkinGuests.forEach(checkinGuest => {
          const oldCheckinGuest = state.checkinRequirements.checkinGuests.find(
            oldCheckinGuest => oldCheckinGuest.id == checkinGuest.id
          );
          checkinGuest.finished =
            checkinGuest.finished || (oldCheckinGuest ? oldCheckinGuest.finished : false);
        });
      }
      updateState({ checkinRequirements });
    },
    setGuestForms: guestForms => {
      updateState({ guestForms });
    },
    setOptionalDocumentData: optionalDocumentData => {
      updateState({ optionalDocumentData });
    },
    setNeedsReloadForm: reload => {
      updateState({ needsReloadForm: reload });
    },
    setBookingCustomParameters: (bookingCustomParameters, checkinGuestId) => {
      if (checkinGuestId)
        updateState({
          bookingCustomParameters: {
            ...state.bookingCustomParameters,
            [checkinGuestId]: bookingCustomParameters,
          },
        });
      else updateState({ bookingCustomParameters });
    },
    clearGuestCustomParameters: checkinGuestId => {
      const bookingCustomParameters = JSON.parse(JSON.stringify(state.bookingCustomParameters));
      delete bookingCustomParameters[checkinGuestId];
      updateState({ bookingCustomParameters });
    },
    setGuestFields: (checkinGuestId, fields, language) => {
      updateState({ fields: { [checkinGuestId]: { fields, language } } });
    },
    clearGuestFieldValues: checkinGuestId => {
      updateState({ formValues: { ...state.formValues, [checkinGuestId]: {} } });
    },
    setGuestFieldValues: (checkinGuestId, values) => {
      updateState({ formValues: { ...state.formValues, [checkinGuestId]: values } });
    },
    setGuestFieldOCRValues: (checkinGuestId, values) => {
      updateState({ ocrValues: { ...state.ocrValues, [checkinGuestId]: values } });
    },
    setGuestFieldAndOCRValues: (checkinGuestId, values) => {
      const newState = {
        ocrValues: { ...state.ocrValues, [checkinGuestId]: values.ocrValues },
        formValues: { ...state.formValues, [checkinGuestId]: values.formValues },
      };
      if (values.ocrValues.reloadGuestForm) newState.needsReloadForm = values.ocrValues;
      updateState(newState);
    },
    setGuestSignature: (checkinGuestId, guestSignature) => {
      updateState({ signatures: { ...state.signatures, [checkinGuestId]: guestSignature } });
    },
    setGuestSurveys: (checkinGuestId, guestSurveys) => {
      updateState({ surveys: { ...state.surveys, [checkinGuestId]: guestSurveys } });
    },
    setGuestFinished: (checkinGuestId, finished) => {
      const newCheckinRequirements = { ...state.checkinRequirements };
      const checkinGuest = newCheckinRequirements.checkinGuests.find(
        checkinGuest => checkinGuest.id == checkinGuestId
      );
      checkinGuest.finished = finished;
      checkinGuest.isLocalState = true;
      updateState({ checkinRequirements: newCheckinRequirements });
    },
    allGuestsFinished: () => {
      return state.checkinRequirements.checkinGuests.every(checkinGuest => !!checkinGuest.finished);
    },
    getAllGuests: () => {
      return state.checkinRequirements.checkinGuests;
    },
    configNewCheckin: (hotelId, bookingToCheckin) => {
      sessionStorage.removeItem(sessionStorageKey);
      setState({ ...defaultState, hotelId, bookingToCheckin });
    },
    clear: () => {
      setState(defaultState);
    },
    setRoomUpgradeCompleted: value => {
      updateState({ roomUpgradeCompleted: value });
    },
    setUpsellCompleted: value => {
      updateState({ upsellCompleted: value });
    },
    setDynamicForms: guestFormFieldsResponse => {
      updateState({
        useDynamicForms: guestFormFieldsResponse.useDynamicForms,
        hotelCountry: guestFormFieldsResponse.hotelCountry,
        dynamicForms: {
          domesticFields: guestFormFieldsResponse.domesticFields,
          nonDomesticFields: guestFormFieldsResponse.nonDomesticFields,
        },
      });
    },
    setHighlightedCountries: highlightedCountries => {
      updateState({ highlightedCountries: highlightedCountries || [] });
    },
    setIsPushGuestDeeplink: ({ isPushGuestDeeplink = true, pushId }) => {
      updateState({ isPushGuestDeeplink, pushId });
    },
    setUpsellV2RoomDetailVisible: value => {
      updateState({ upsellV2RoomDetailVisible: value });
    },
    setCrossSellV2ItemDetailVisible: value => {
      updateState({ crossSellV2ItemDetailVisible: value });
    },
    setHolderAddressData: addressData => {
      updateState({ holderAddressData: addressData });
    },
    setHolderContactData: contactData => {
      updateState({ holderContactData: contactData });
    },
    onBackPress: () => {
      try {
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(state));
      } catch (error) {
        console.error(error);
      }
    },
  };

  useEffect(() => {
    // Gestión multireservas con sesion via proy

    // Leemos los UUIDs existentes en localStorage
    const stored = localStorage.getItem('cf-booking');
    const currentUuids = stored ? stored.split(',').map(uuid => uuid.trim()) : [];

    // Preparamos los UUIDs nuevos a añadir
    let newUuids = [];
    if (state.checkinRequirements?.booking) {
      newUuids = [state.checkinRequirements.booking.uuid];
    } else if (state.bookings?.length) {
      newUuids = state.bookings.map(b => b.uuid);
    }

    // Filtramos los que no estén ya en currentUuids
    const uuidsToAdd = newUuids.filter(uuid => !currentUuids.includes(uuid));

    // Si hay algo nuevo, los añadimos y guardamos de nuevo
    if (uuidsToAdd.length > 0) {
      const updatedUuids = [...currentUuids, ...uuidsToAdd];
      localStorage.setItem('cf-booking', updatedUuids.join(','));
    }
  }, [state.checkinRequirements, state.bookings]);

  useEffect(() => {
    const persistedBookingContext = sessionStorage.getItem(sessionStorageKey);
    if (persistedBookingContext) {
      updateState(JSON.parse(persistedBookingContext));
    }
  }, []);

  useEffect(() => {
    if (router.query.hotelId) {
      if (state.hotelId && state.hotelId != router.query.hotelId) {
        sessionStorage.clear();
        setState({ ...defaultState, hotelId: router.query.hotelId });
      } else {
        updateState({ hotelId: router.query.hotelId });
      }
    }
  }, [router.query.hotelId]);

  // FIXME:
  // Este proceso afecta gravemente en el rendimiento, pero actualmente
  // es necesario para mantener el estado sincronizado y evitar problemas de
  // navegación al salir de la app de check-in y redirigir el flujo a otros módulos
  // (por ejemplo, el de pagos).
  //
  // Es necesario refactorizarlo para que el guardado en localStorage ocurra solo
  // cuando sea estrictamente necesario y no en cada llamada a updateState.
  function updateState(newState) {
    setState(previousState => {
      const finalState = { ...previousState, ...newState };
      try {
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(finalState));
      } catch (error) {
        console.error(error);
      }
      return finalState;
    });
  }

  return <BookingContext.Provider value={{ state, actions }}>{children}</BookingContext.Provider>;
}
